import { RetailerProgramTags } from '@/components/shared/ProgramTags';
import { Button, Card, HSpacer, Text, TextBadge, VSpacer } from '@/components/DesignSystem';
import { RetailerAvatar } from "@/components/DesignSystem/Avatar/RetailerAvatar";
import { useGetRetailerById } from '@/hooks/useRetailer';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import EditIcon from '@mui/icons-material/EditOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { Stack, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import AddRetailer from './RetailerFormDialog';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { downloadCsv } from '@/utilities/Export';
import {
  IntegrationImportFailureBadge,
} from "@/pages/Admin/HierarchyOfRetailers/Integrations/IntegrationImportFailureBadge";
import { useFeatureFlags } from '@/hooks/useFeatureFlag';
import { FeatureFlag } from '@shared/enums/FeatureFlag';

const RetailerDetailsCard = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { isInternal } = useAuthentication();
  const [isEditRetailer, setIsEditRetailer] = useState(false);
  const [showExportConfirmationDialog, setShowExportConfirmationDialog] = useState(false);
  const { isFlagOn } = useFeatureFlags();
  const isCanadianAdaptationsEnabled = isFlagOn(FeatureFlag.CanadianAdaptations);

  const editRetailer = () => {
    setIsEditRetailer(true);
  };

  const {
    mutateAsync: exportCustomersForRetailer,
    isLoading: isExporting,
  } = useMutation(
    async () => {
      const csv = await RetailerApi.exportChurnCustomersForRetailer(id ?? '');
      const dateString = new Date().toISOString().slice(0, 10);
      const retailerName = retailer?.name?.replace(/\s+/g, '-').toLowerCase();
      downloadCsv(csv, `${retailerName}-churn-export-${dateString}`);
    },
  );

  const { retailer } = useGetRetailerById(id ?? '');

  return (
    <Card testID="retailer-details-card">
      <Stack direction="row">
        <RetailerAvatar retailer={retailer} />
        <HSpacer size="5" />
        <Stack direction="column" spacing={0.7} width="100%">
          <Text category="overline" sx={{ color: theme.palette.text.secondary }}>
            ID# {retailer?.id}
          </Text>
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
          >
            <Stack direction="column" spacing={0.5}>
              <Stack alignItems="center" direction="row" spacing={1}>
                <Text category="headline-medium">
                  {retailer?.name}
                </Text>
                <RetailerProgramTags
                  programs={retailer?.rewardsPrograms}
                  testID="retailer-details-program-tags"
                />
              </Stack>
              {isCanadianAdaptationsEnabled && retailer?.country && (
                <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
                  {retailer.country}
                </Text>
              )}
            </Stack>
            <Stack direction="row" spacing={2}>
              {isInternal && retailer?.isChurnEnabled && (
                <Button
                  onClick={() => setShowExportConfirmationDialog(true)}
                  startIcon={<ExitToAppIcon />}
                  testID='export-churn'
                  variant='text'
                >
                  Export customer churn CSV
                </Button>
              )}
              <Button
                onClick={editRetailer}
                startIcon={<EditIcon />}
                testID='edit-details'
                variant='outlined'
              >
                Edit
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      {!!retailer?.isErpIntegrationEnabled && (
        <Stack width="fit-content">
          <VSpacer size="3" />
          <TextBadge
            backgroundColor={theme.palette.info.main}
            text="ERP integration"
          />
        </Stack>
      )}
      {!!retailer?.hasIntegrationFailures && (
        <>
          <VSpacer size="3" />
          <IntegrationImportFailureBadge />
        </>
      )}
      {isEditRetailer && (
        <AddRetailer
          initialValue={retailer}
          isAddRetailerOpen={isEditRetailer}
          setIsAddRetailerOpen={setIsEditRetailer}
        />
      )}
      {showExportConfirmationDialog && (
        <ConfirmDialog
          cancelText="Go back"
          confirmText="Export"
          isLoading={isExporting}
          onCancel={() => setShowExportConfirmationDialog(false)}
          onConfirm={async () => {
            await exportCustomersForRetailer();
            setShowExportConfirmationDialog(false);
          }}
          open
          showCloseButton={false}
          testID="export-churn-confirmation-dialog"
        >
          This export will include all customer churn data for {retailer?.name || 'this retailer'}.
        </ConfirmDialog>
      )}
    </Card>
  );
};

export default RetailerDetailsCard;
